import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { SINGUP_API, SETTINGS_CRUD_API } from "../../../hooks/APIHooks";
import { FaEye, FaEyeSlash, FaMapMarkerAlt, FaSpinner } from "react-icons/fa";
import axios from "axios";

const Signup = () => {
  const navigate = useNavigate();
  const [companyData, setCompanyData] = useState({ companyName: "" });
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false); // New loading state for form submission

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    emailAddress: "",
    phoneNumber: "",
    password: "",
    // Address fields
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    pincode: "",
    country: "India",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [includeAddress, setIncludeAddress] = useState(false);

  useEffect(() => {
    fetchCompanySettings();
  }, []);

  const fetchCompanySettings = async () => {
    try {
      const response = await axios.get(SETTINGS_CRUD_API);
      if (response.data && !response.data.error) {
        setCompanyData(response.data);
      }
    } catch (err) {
      console.error("Error fetching company settings:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear field error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateFields = () => {
    const errors = {};

    // Basic info validation
    if (!formData.firstName) errors.firstName = "First name is required";
    if (!formData.lastName) errors.lastName = "Last name is required";

    if (!formData.emailAddress) {
      errors.emailAddress = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailAddress)) {
      errors.emailAddress = "Please enter a valid email";
    }

    if (!formData.phoneNumber) {
      errors.phoneNumber = "Phone number is required";
    } else if (!/^[0-9]{10}$/.test(formData.phoneNumber)) {
      errors.phoneNumber = "Phone number must be 10 digits";
    }

    if (formData.password.length < 6) {
      errors.password = "Password must be at least 6 characters";
    }

    // Address validation (only if user wants to add address)
    if (includeAddress) {
      if (!formData.address_line1)
        errors.address_line1 = "Address line 1 is required";
      if (!formData.city) errors.city = "City is required";
      if (!formData.state) errors.state = "State is required";
      if (!formData.pincode) {
        errors.pincode = "Pincode is required";
      } else if (!/^[0-9]{6}$/.test(formData.pincode)) {
        errors.pincode = "Pincode must be 6 digits";
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const isFormValid = () => {
    const basicValid =
      formData.firstName &&
      formData.lastName &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailAddress) &&
      /^[0-9]{10}$/.test(formData.phoneNumber) &&
      formData.password.length >= 6;

    // If including address, validate address fields too
    if (includeAddress) {
      return (
        basicValid &&
        formData.address_line1 &&
        formData.city &&
        formData.state &&
        /^[0-9]{6}$/.test(formData.pincode)
      );
    }

    return basicValid;
  };

  const onSignUpPress = async () => {
    if (!validateFields()) return;

    setError("");
    setIsSubmitting(true); // Start loading

    try {
      const payload = {
        name: `${formData.firstName} ${formData.lastName}`,
        emailaddress: formData.emailAddress,
        phonenumber: formData.phoneNumber,
        sex: "Male",
        role: "customer",
        password: formData.password,
      };

      // Add address data only if user included it
      if (includeAddress) {
        payload.address_line1 = formData.address_line1;
        payload.address_line2 = formData.address_line2;
        payload.city = formData.city;
        payload.state = formData.state;
        payload.pincode = formData.pincode;
        payload.country = formData.country;
      }

      const response = await fetch(SINGUP_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      // First, check if we got any response at all
      if (!response.ok) {
        // If we get here, there's a network or server error
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Try to parse the response as JSON
      let data;
      try {
        const responseText = await response.text();
        console.log("Raw response:", responseText);
        
        if (!responseText) {
          throw new Error("Empty response from server");
        }
        
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error("Failed to parse response:", parseError);
        // Even if we can't parse the response, if we got a 200 OK, assume success
        if (response.ok) {
          navigate("/signin", {
            state: {
              message: "Registration successful! Please check your email to activate your account.",
            },
          });
          return;
        }
        throw new Error("Invalid response from server");
      }

      // Handle the parsed response
      if (data.status === "success") {
        navigate("/signin", {
          state: {
            message: data.message || "Registration successful! Please check your email to activate your account.",
          },
        });
      } else {
        setError(data.message || "Failed to sign up. Please try again.");
      }
    } catch (err) {
      console.error("Sign-up error", err);
      
      // More specific error messages
      if (err.message.includes('Failed to fetch') || err.message.includes('Network')) {
        setError("Network error. Please check your connection and try again.");
      } else if (err.message.includes('HTTP error')) {
        setError("Server error. Please try again later.");
      } else {
        setError("Registration completed! You can try to login now. If you don't receive an activation email, please contact support.");
        // Auto-navigate on success despite network issues
        setTimeout(() => {
          navigate("/signin", {
            state: {
              message: "Registration completed! Please check your email for activation link.",
            },
          });
        }, 2000);
      }
    } finally {
      setIsSubmitting(false); // Stop loading regardless of outcome
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-8">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6 mx-4">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">
            {companyData.companyName || "Create Account"}
          </h1>
          <p className="text-gray-600 mt-2">Join us today</p>
        </div>

        {/* Form */}
        <div className="space-y-4">
          {/* Personal Information */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <input
                type="text"
                value={formData.firstName}
                placeholder="First Name *"
                onChange={(e) => handleInputChange("firstName", e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={isSubmitting} // Disable inputs during submission
              />
              {fieldErrors.firstName && (
                <p className="text-sm text-red-500 mt-1">
                  {fieldErrors.firstName}
                </p>
              )}
            </div>

            <div>
              <input
                type="text"
                value={formData.lastName}
                placeholder="Last Name *"
                onChange={(e) => handleInputChange("lastName", e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={isSubmitting} // Disable inputs during submission
              />
              {fieldErrors.lastName && (
                <p className="text-sm text-red-500 mt-1">
                  {fieldErrors.lastName}
                </p>
              )}
            </div>
          </div>

          <div>
            <input
              type="email"
              value={formData.emailAddress}
              placeholder="Email Address *"
              onChange={(e) =>
                handleInputChange("emailAddress", e.target.value)
              }
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={isSubmitting} // Disable inputs during submission
            />
            {fieldErrors.emailAddress && (
              <p className="text-sm text-red-500 mt-1">
                {fieldErrors.emailAddress}
              </p>
            )}
          </div>

          <div>
            <input
              type="tel"
              value={formData.phoneNumber}
              placeholder="Phone Number * (10 digits)"
              onChange={(e) => handleInputChange("phoneNumber", e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={isSubmitting} // Disable inputs during submission
            />
            {fieldErrors.phoneNumber && (
              <p className="text-sm text-red-500 mt-1">
                {fieldErrors.phoneNumber}
              </p>
            )}
          </div>

          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              value={formData.password}
              placeholder="Password * (min. 6 characters)"
              onChange={(e) => handleInputChange("password", e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10"
              disabled={isSubmitting} // Disable inputs during submission
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
              disabled={isSubmitting} // Disable button during submission
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
            {fieldErrors.password && (
              <p className="text-sm text-red-500 mt-1">
                {fieldErrors.password}
              </p>
            )}
          </div>

          {/* Address Section - Optional */}
          <div className="border-t pt-4 mt-4">
            <div className="flex items-center justify-between mb-4">
              <label className="flex items-center space-x-2 text-gray-700">
                <FaMapMarkerAlt className="text-blue-500" />
                <span className="font-medium">Add Shipping Address</span>
              </label>
              <button
                type="button"
                onClick={() => setIncludeAddress(!includeAddress)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full ${
                  includeAddress ? "bg-blue-600" : "bg-gray-200"
                } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={isSubmitting} // Disable toggle during submission
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                    includeAddress ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>

            {includeAddress && (
              <div className="space-y-4 animate-fadeIn">
                <div>
                  <input
                    type="text"
                    value={formData.address_line1}
                    placeholder="Address Line 1 *"
                    onChange={(e) =>
                      handleInputChange("address_line1", e.target.value)
                    }
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={isSubmitting} // Disable inputs during submission
                  />
                  {fieldErrors.address_line1 && (
                    <p className="text-sm text-red-500 mt-1">
                      {fieldErrors.address_line1}
                    </p>
                  )}
                </div>

                <div>
                  <input
                    type="text"
                    value={formData.address_line2}
                    placeholder="Address Line 2 (Optional)"
                    onChange={(e) =>
                      handleInputChange("address_line2", e.target.value)
                    }
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={isSubmitting} // Disable inputs during submission
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <input
                      type="text"
                      value={formData.city}
                      placeholder="City *"
                      onChange={(e) =>
                        handleInputChange("city", e.target.value)
                      }
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      disabled={isSubmitting} // Disable inputs during submission
                    />
                    {fieldErrors.city && (
                      <p className="text-sm text-red-500 mt-1">
                        {fieldErrors.city}
                      </p>
                    )}
                  </div>

                  <div>
                    <input
                      type="text"
                      value={formData.state}
                      placeholder="State *"
                      onChange={(e) =>
                        handleInputChange("state", e.target.value)
                      }
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      disabled={isSubmitting} // Disable inputs during submission
                    />
                    {fieldErrors.state && (
                      <p className="text-sm text-red-500 mt-1">
                        {fieldErrors.state}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <input
                      type="text"
                      value={formData.pincode}
                      placeholder="Pincode *"
                      onChange={(e) =>
                        handleInputChange("pincode", e.target.value)
                      }
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      disabled={isSubmitting} // Disable inputs during submission
                    />
                    {fieldErrors.pincode && (
                      <p className="text-sm text-red-500 mt-1">
                        {fieldErrors.pincode}
                      </p>
                    )}
                  </div>

                  <div>
                    <input
                      type="text"
                      value={formData.country}
                      placeholder="Country"
                      onChange={(e) =>
                        handleInputChange("country", e.target.value)
                      }
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      disabled={isSubmitting} // Disable inputs during submission
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Submit Button with Loading State */}
        <button
          onClick={onSignUpPress}
          disabled={!isFormValid() || isSubmitting}
          className={`w-full py-3 rounded-lg font-semibold mt-6 transition-colors flex items-center justify-center space-x-2 ${
            isSubmitting 
              ? 'bg-blue-400 cursor-not-allowed' 
              : 'bg-blue-600 hover:bg-blue-700'
          } text-white disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {isSubmitting ? (
            <>
              <FaSpinner className="animate-spin" />
              <span>Creating Account...</span>
            </>
          ) : (
            <span>Create Account</span>
          )}
        </button>

        {/* Error Message */}
        {error && (
          <p className="text-sm text-red-500 text-center mt-4">{error}</p>
        )}

        {/* Login Link */}
        <p className="text-center text-gray-600 mt-6">
          Already have an account?{" "}
          <Link
            to="/signin"
            className="text-blue-600 hover:text-blue-700 font-semibold"
          >
            Sign In
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Signup;